import { useEffect, useRef, useState } from 'react';

import { type LotteryTicketType } from '@/common/types/lottery';
import { selectTicketFromLastLotteryWinner } from '@/redux/selectors/lotterySelector';
import { useAppSelector } from '@/redux/store';

import { animateDuration } from '../config';

const useCurrentLotteryTier = (): LotteryTicketType =>
  useAppSelector((state) => state.lottery.currentTier.name);

interface UseWinnerTicketStepsReturnType {
  filteredValue: string | null;
  ticket: string | undefined;
}

const useWinnerTicketSteps = (): UseWinnerTicketStepsReturnType => {
  const ticket = useAppSelector(selectTicketFromLastLotteryWinner);
  const isAnimationActive = useAppSelector(
    (state) => state.lottery.isLotteryHappeningNow,
  );
  const [filteredValue, setFilteredValue] = useState<string | null>(null);
  const charsCountRef = useRef<number>(1);
  const timerId = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!ticket) return;

    if (isAnimationActive) {
      const changeFilteredValue = (): void => {
        setFilteredValue(ticket.slice(0, charsCountRef.current));

        if (charsCountRef.current < ticket.length) {
          charsCountRef.current += 1;
        } else {
          clearInterval(timerId.current);
        }
      };

      changeFilteredValue();

      timerId.current = setInterval(
        changeFilteredValue,
        (animateDuration * 1000) / ticket.length,
      );
    } else {
      setFilteredValue(ticket);
    }

    return (): void => {
      charsCountRef.current = 1;
      clearInterval(timerId.current);
      setFilteredValue(null);
    };
  }, [ticket, isAnimationActive]);

  return { filteredValue, ticket };
};

export { useCurrentLotteryTier, useWinnerTicketSteps };
